import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../hooks/useGetGeneralAppData';
import CompanySettingsService from '../../../../../services/CompanySettingsService';
import parseCurrencyStringToFloat from '../../../../../utils/parseCurrencyStringToFloat';
import { EmailsObjectType, KindOfVtSpTransType, MaxContestsAllowedType, PartnerType, TabsOptionsType, TechnologyType, TransfersBehaviorType, VT_PURCHASE_PROVIDER_NOT_INFORMED, VT_PURCHASE_PROVIDER_SAME_AS_SOURCE, VtProviderType } from '../../types';

interface UseSettingsSubmitI {
  isFirstSetup: boolean;
  extractEmails: EmailsObjectType[];
  routingEmails: EmailsObjectType[];
  routingNoResultEmails: EmailsObjectType[];
  financialEmails: EmailsObjectType[];
  willIntegrateWithPayroll: boolean;
  technology: TechnologyType;
  losesOwnershipWithoutBoarding: boolean;
  daysAllowedWithoutBoarding: number | string;
  sendAnswerOfClaimToCollabEmail: boolean;
  allowCollabToSeeLetter: boolean;
  allowMultipleConsults: boolean;
  usesCustomLogo: boolean;
  integrateWithWs: boolean;
  vtProvider: VtProviderType;
  vtPurchaseProvider: VtProviderType;
  hasCustomLogo: boolean;
  showTicketsAtNotOptimizedResults: boolean;
  hasCustomTextAtLetter: boolean;
  customTextAtLetter: string;
  partner: PartnerType;
  secondPartner: PartnerType;
  directResult: boolean;
  maxContestsAllowed: MaxContestsAllowedType;
  copyGoingToReturn: boolean;
  hasAffreightment: boolean;
  usaHorarioFretado: boolean;
  hasKmLimit: boolean;
  isMyLinkFull: boolean;
  kmLimit: number[];
  walkDistance: number[];
  transfersBehavior: TransfersBehaviorType;
  compareWithDesiredVtValue: boolean;
  utilizaTetoValorPadraoEmp: boolean;
  desiredVtValue: string;
  chooseDefaultValueForRoutings: boolean;
  defaultValueForRoutings: string;
  kindOfVtSpTrans: KindOfVtSpTransType;
  handleChangeActiveTab: (activeTab: TabsOptionsType) => void;
  imageFile: string | File | undefined;
  loadPage: () => Promise<void>;
  currentServices: { codServ: number, servico: string }[];
  defaultWorkplaceUsesIntegrationWs: boolean;
  changesWpDataByWs: boolean;
  isPartner: boolean;
  usesLogoAtEmail: boolean;
  verifySalaryRJ: boolean;
  vtItensRelationsRequestEmails: EmailsObjectType[];
  canRequestVtItemRelation: boolean;
  hasWebhook: boolean;
  kindOfVtAesa: string;
  aboveDesiredValueReturnsBiasTag: boolean;
  hasResultReadyWebhook: boolean;
  hasResultOutOfScopeWebhook: boolean;
  integrator: PartnerType;
  mustSendAddressProof: boolean;
  particularities: string;
  usaRodoviarioValZero: boolean;
  kmRodoviarioValZero: number | string;
  geraNFSeparada: boolean;
}

export default function useSettingsSubmit({
  isFirstSetup,
  extractEmails,
  routingEmails,
  routingNoResultEmails,
  financialEmails,
  willIntegrateWithPayroll,
  technology,
  losesOwnershipWithoutBoarding,
  daysAllowedWithoutBoarding,
  sendAnswerOfClaimToCollabEmail,
  allowCollabToSeeLetter,
  allowMultipleConsults,
  usesCustomLogo,
  integrateWithWs,
  vtProvider,
  vtPurchaseProvider,
  hasCustomLogo,
  showTicketsAtNotOptimizedResults,
  hasCustomTextAtLetter,
  customTextAtLetter,
  partner,
  secondPartner,
  directResult,
  maxContestsAllowed,
  copyGoingToReturn,
  hasAffreightment,
  usaHorarioFretado,
  hasKmLimit,
  kmLimit,
  walkDistance,
  transfersBehavior,
  compareWithDesiredVtValue,
  utilizaTetoValorPadraoEmp,
  desiredVtValue,
  chooseDefaultValueForRoutings,
  defaultValueForRoutings,
  kindOfVtSpTrans,
  isMyLinkFull,
  handleChangeActiveTab,
  imageFile,
  loadPage,
  currentServices,
  defaultWorkplaceUsesIntegrationWs,
  changesWpDataByWs,
  isPartner,
  usesLogoAtEmail,
  verifySalaryRJ,
  vtItensRelationsRequestEmails,
  canRequestVtItemRelation,
  hasWebhook,
  kindOfVtAesa,
  aboveDesiredValueReturnsBiasTag,
  hasResultReadyWebhook,
  hasResultOutOfScopeWebhook,
  integrator,
  mustSendAddressProof,
  particularities,
  usaRodoviarioValZero,
  kmRodoviarioValZero,
  geraNFSeparada,
}: UseSettingsSubmitI) {
  const [isSubmitingCompanySettings, setIsSubmittingCompanySettings] = useState(false);

  const { userProfile, userCode, companyCode } = useGetGeneralAppData();
  const { apiCall } = useApiCall();

  const setupCompany = useCallback(async ({ logo }: { logo?: boolean }) => {
    const willUseSomeCustomLogo = usesCustomLogo || hasCustomLogo;
    const hasLogoUploaded = !!imageFile;

    const transfersbehaviorMap: {[key: string]: number} = {
      'unlimited': 0,
      'minimun': 1,
      'default': 2,
    };

    const hasEmailsListToRecord = extractEmails.length > 0 || routingEmails.length > 0 || routingNoResultEmails.length > 0 || vtItensRelationsRequestEmails.length > 0 || financialEmails.length > 0;

    const extractEmailsMapped = extractEmails?.map((email) => ({
      endMail: email.email,
      codProcesso: 1,
      nomeUsuario: email.emailOwnerName,
    }));

    const routingEmailsMapped = routingEmails?.map((email) => ({
      endMail: email.email,
      codProcesso: 2,
      nomeUsuario: email.emailOwnerName,
    }));

    const routingNoResultEmailsMapped = routingNoResultEmails?.map((email) => ({
      endMail: email.email,
      codProcesso: 3,
      nomeUsuario: email.emailOwnerName,
    }));

    const vtItensRelationsRequestEmailsMapped = vtItensRelationsRequestEmails?.map((email) => ({
      endMail: email.email,
      codProcesso: 4,
      nomeUsuario: email.emailOwnerName,
    }));

    const financialEmailsMapped = financialEmails?.map((email) => ({
      endMail: email.email,
      codProcesso: 5,
      nomeUsuario: email.emailOwnerName,
    }));

    const emailsListToSendToApi = [
      ...extractEmailsMapped,
      ...routingEmailsMapped,
      ...routingNoResultEmailsMapped,
      ...vtItensRelationsRequestEmailsMapped,
      ...financialEmailsMapped,
    ];

    const envelopadoraFornecedor = (() => {
      if (!vtProvider.value || vtProvider.value === VT_PURCHASE_PROVIDER_NOT_INFORMED) {
        return 0;
      }

      if (
        !vtPurchaseProvider.value
        || vtPurchaseProvider.value === VT_PURCHASE_PROVIDER_NOT_INFORMED
      ) {
        return 0;
      }

      if (vtPurchaseProvider.value === VT_PURCHASE_PROVIDER_SAME_AS_SOURCE) {
        return Number(vtProvider.value);
      }

      return Number(vtPurchaseProvider.value);
    })();

    await apiCall({
      apiToCall: CompanySettingsService.editOrSetCompanySettings,
      onStartLoad: () => setIsSubmittingCompanySettings(true),
      onEndLoad: () => setIsSubmittingCompanySettings(false),
      catchMessage: 'Não foi possível salvar as configurações. Por favor, tente novamente',
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode },
      reqBody: JSON.stringify({
        'codUsu': userCode,
        'perfilUsu': userProfile,
        'codEmpresa': companyCode,
        'tipoAcao': isFirstSetup ? 11 : 12,
        'listaEmails': hasEmailsListToRecord,
        'dados': {
          'emailAceite': routingEmails.length > 0,
          'emailExtPA': extractEmails.length > 0,
          'emailRotSemResultado': routingNoResultEmails.length > 0,
          'utilizaTeto': compareWithDesiredVtValue,
          'valorTeto': compareWithDesiredVtValue ? parseCurrencyStringToFloat(desiredVtValue) : 0,
          'utilizaTetoValorPadraoEmp': compareWithDesiredVtValue ? utilizaTetoValorPadraoEmp : false,
          'acimaTetoBiasTag': compareWithDesiredVtValue ? aboveDesiredValueReturnsBiasTag : false,
          'espelhamento': copyGoingToReturn,
          'usaVTInicial': chooseDefaultValueForRoutings,
          'valVTInicial': chooseDefaultValueForRoutings ? parseCurrencyStringToFloat(defaultValueForRoutings) : 0,
          'usaFreta': hasAffreightment,
          'usaHorarioFretado': usaHorarioFretado,
          'usaLimKm': hasKmLimit,
          usaMyLinkFull: isMyLinkFull,
          'kmLimite': hasKmLimit ? kmLimit[1] * 1000 : 0,
          'opcaoTextoCarta': hasCustomTextAtLetter ? 1 : 0,
          'envelopadora': vtProvider.value,
          'nomeEnvelopadora': vtProvider.label,
          'envelopadoraFornecedor': envelopadoraFornecedor,
          'textoCarta': hasCustomTextAtLetter ? customTextAtLetter : '',
          'logoCarta': logo || hasCustomLogo,
          'tipoBaldeacao': transfersbehaviorMap[transfersBehavior],
          'regraVTBilSemTx': kindOfVtSpTrans === 'common' ? true : false,
          'emailFuncWebC': sendAnswerOfClaimToCollabEmail,
          'cartaLiberada': allowCollabToSeeLetter,
          'reprocessaMyLink': allowMultipleConsults,
          'envioDadosWCAceite': integrateWithWs,
          'MyLinkLogo': usesCustomLogo,
          'bloqueiaEmbCAF': losesOwnershipWithoutBoarding,
          'diasBloqueioCAF': losesOwnershipWithoutBoarding ? daysAllowedWithoutBoarding : 0,
          'conexaoFolhaPg': willIntegrateWithPayroll,
          'codTecFolhaPg': willIntegrateWithPayroll ? technology.value : 0,
          'nomeTecFolhaPg': willIntegrateWithPayroll ? technology.label : '',
          'codParceiro': partner.value,
          'nomeParceiro': partner.value ? partner.label : '',
          'codParceiro2': secondPartner.value,
          'nomeParceiro2': secondPartner.value ? secondPartner.label : '',
          'codIntegrador': integrator.value,
          'nomeIntegrador': integrator.value ? integrator.label : '',
          'emailDirect': directResult,
          'notificaFuncDirect': directResult,
          'cartaEmBranco': !showTicketsAtNotOptimizedResults,
          'totalMaxReclama': maxContestsAllowed,
          'distInicial': walkDistance[1],
          'emailLogo': hasCustomLogo,
          'regraBilSemIntegraRJ': !defaultWorkplaceUsesIntegrationWs,
          'alteraDadosLT': changesWpDataByWs,
          'tipoParceiro': isPartner,
          ...(isPartner ? { codEmpresaParceiro: companyCode }: {}),
          'temLogoEmail': usesLogoAtEmail,
          'verificaSalarioRJ': verifySalaryRJ,
          'atualizaCodVTEnvelp': canRequestVtItemRelation || vtItensRelationsRequestEmails.length > 0,
          'usaWebhook': hasWebhook,
          'usaWebhook2': hasResultReadyWebhook,
          'usaWebhookForaAbrang': hasResultOutOfScopeWebhook,
          'regraVTSemTxAesa': kindOfVtAesa !== 'vt',
          'anexarComprovanteEnd': mustSendAddressProof,
          'particularidades': particularities,
          'usaRodoviarioValZero': usaRodoviarioValZero,
          'kmRodoviarioValZero': usaRodoviarioValZero ? Number(kmRodoviarioValZero) * 1000 : 0,
          'geraNFSeparada': geraNFSeparada,
          'geraNFSeparadaMatriz': false,
        },
        'emailsCli': emailsListToSendToApi ?? [],
        servicosContrato: currentServices?.map((x) => ({ codServ: x.codServ })) || [],
      }),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível salvar as configurações. Por favor, tente novamente (${response.msg})`);
          return;
        }
        toast.success('As configurações foram salvas com sucesso!');
        handleChangeActiveTab('routingRules');
        loadPage();
        if (willUseSomeCustomLogo && !hasLogoUploaded) {
          toast.warn('Verificamos que você selecionou que irá usar logomarca própria em algum campo, porém essa empresa ainda não possui nenhuma logomarca configurada. Por favor, realize o upload da logomarca.');
          handleChangeActiveTab('customLogo');
          return;
        }
      }
    });
  }, [usesCustomLogo, hasCustomLogo, imageFile, extractEmails, financialEmails, routingEmails, routingNoResultEmails, vtItensRelationsRequestEmails, apiCall, userCode, userProfile, companyCode, isFirstSetup, compareWithDesiredVtValue, utilizaTetoValorPadraoEmp, desiredVtValue, aboveDesiredValueReturnsBiasTag, copyGoingToReturn, chooseDefaultValueForRoutings, defaultValueForRoutings, hasAffreightment, usaHorarioFretado, hasKmLimit, isMyLinkFull, kmLimit, hasCustomTextAtLetter, vtProvider.value, vtProvider.label, vtPurchaseProvider.value, customTextAtLetter, transfersBehavior, kindOfVtSpTrans, sendAnswerOfClaimToCollabEmail, allowCollabToSeeLetter, allowMultipleConsults, integrateWithWs, losesOwnershipWithoutBoarding, daysAllowedWithoutBoarding, willIntegrateWithPayroll, technology.value, technology.label, partner.value, partner.label, secondPartner.value, secondPartner.label, integrator.value, integrator.label, directResult, showTicketsAtNotOptimizedResults, maxContestsAllowed, walkDistance, defaultWorkplaceUsesIntegrationWs, changesWpDataByWs, isPartner, usesLogoAtEmail, verifySalaryRJ, canRequestVtItemRelation, hasWebhook, hasResultReadyWebhook, hasResultOutOfScopeWebhook, kindOfVtAesa, mustSendAddressProof, particularities, usaRodoviarioValZero, kmRodoviarioValZero, geraNFSeparada, currentServices, handleChangeActiveTab, loadPage]);

  return {
    setupCompany,
    isSubmitingCompanySettings,
  };
}
